import React, { useState } from 'react';
import CustomDropdown from '../CustomDropdown';
import {
  EXTERIOR_FINISH_BRAND_OPTIONS,
  EXTERIOR_FINISH_STYLE_OPTIONS,
  EXTERIOR_FINISH_TYPE_OPTIONS,
  POPULAR_VEHICLE_COLORS,
  type ExteriorFinishType,
} from '../../types/exteriorFinish';

export interface ExteriorFinishFormValues {
  color: string;
  exterior_finish_type: ExteriorFinishType;
  exterior_finish_color: string;
  exterior_finish_brand: string;
  exterior_finish_style: string;
}

interface ExteriorFinishFieldsProps {
  values: ExteriorFinishFormValues;
  onChange: (field: keyof ExteriorFinishFormValues, value: string) => void;
  disabled?: boolean;
  errors?: Partial<Record<keyof ExteriorFinishFormValues, string>>;
}

const ExteriorFinishFields: React.FC<ExteriorFinishFieldsProps> = ({
  values,
  onChange,
  disabled = false,
  errors = {},
}) => {
  const [isCustomFactoryColor, setIsCustomFactoryColor] = useState(false);
  const [isCustomFinishColor, setIsCustomFinishColor] = useState(false);
  const hasWrapOrPpf = values.exterior_finish_type !== 'paint';
  const factoryColorLabel = hasWrapOrPpf ? 'Factory / base color' : 'Color';

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-gray-300 mb-2">Exterior finish</p>
        <p className="text-xs text-gray-500 mb-3">
          Show what people actually see on the car. Wraps and PPF sit on top of your factory or base color.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {EXTERIOR_FINISH_TYPE_OPTIONS.map((option) => {
            const selected = values.exterior_finish_type === option.value;
            return (
              <button
                key={option.value}
                type="button"
                disabled={disabled}
                onClick={() => onChange('exterior_finish_type', option.value)}
                className={`rounded-lg border px-3 py-3 text-left transition-colors ${
                  selected
                    ? 'border-orange-500/70 bg-orange-500/10 text-white'
                    : 'border-white/[0.10] bg-[#111]/40 text-gray-300 hover:border-white/[0.18]'
                }`}
              >
                <div className="text-sm font-medium">{option.label}</div>
                <div className="text-xs text-gray-500 mt-1">{option.description}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">{factoryColorLabel}</label>
          {!isCustomFactoryColor ? (
            <CustomDropdown
              value={values.color}
              onChange={(value) => {
                if (value === 'other') {
                  setIsCustomFactoryColor(true);
                  onChange('color', '');
                } else {
                  onChange('color', value);
                }
              }}
              options={[
                { value: '', label: hasWrapOrPpf ? 'Optional base color' : 'Select color (optional)' },
                ...POPULAR_VEHICLE_COLORS.map((color) => ({ value: color, label: color })),
                { value: 'other', label: 'Other (custom)' },
              ]}
              placeholder={hasWrapOrPpf ? 'Optional base color' : 'Select color (optional)'}
              disabled={disabled}
            />
          ) : (
            <div className="space-y-2">
              <input
                type="text"
                value={values.color}
                onChange={(e) => onChange('color', e.target.value)}
                className="input"
                placeholder={hasWrapOrPpf ? 'e.g. Alpine White' : 'Enter custom color'}
                disabled={disabled}
              />
              <button
                type="button"
                onClick={() => {
                  setIsCustomFactoryColor(false);
                  onChange('color', '');
                }}
                className="text-sm text-orange-400 hover:text-orange-300 transition-colors"
                disabled={disabled}
              >
                Back to popular colors
              </button>
            </div>
          )}
        </div>

        {hasWrapOrPpf && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {values.exterior_finish_type === 'wrap' ? 'Wrap color / name' : 'PPF finish'}
              <span className="text-red-400 ml-1">*</span>
            </label>
            {!isCustomFinishColor ? (
              <CustomDropdown
                value={values.exterior_finish_color}
                onChange={(value) => {
                  if (value === 'other') {
                    setIsCustomFinishColor(true);
                    onChange('exterior_finish_color', '');
                  } else {
                    onChange('exterior_finish_color', value);
                  }
                }}
                options={[
                  { value: '', label: 'Select or enter custom' },
                  ...POPULAR_VEHICLE_COLORS.map((color) => ({ value: color, label: color })),
                  { value: 'Clear', label: 'Clear' },
                  { value: 'Stealth', label: 'Stealth / satin PPF' },
                  { value: 'other', label: 'Other (custom)' },
                ]}
                placeholder="What does the car look like now?"
                disabled={disabled}
                error={!!errors.exterior_finish_color}
              />
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={values.exterior_finish_color}
                  onChange={(e) => onChange('exterior_finish_color', e.target.value)}
                  className={`input ${
                    errors.exterior_finish_color ? 'border-red-500' : 'border-white/[0.10]'
                  }`}
                  placeholder={
                    values.exterior_finish_type === 'wrap'
                      ? 'e.g. Satin Dark Grey, Color-shift Purple'
                      : 'e.g. XPEL Ultimate Plus (clear), Stealth grey'
                  }
                  disabled={disabled}
                />
                <button
                  type="button"
                  onClick={() => {
                    setIsCustomFinishColor(false);
                    onChange('exterior_finish_color', '');
                  }}
                  className="text-sm text-orange-400 hover:text-orange-300 transition-colors"
                  disabled={disabled}
                >
                  Back to presets
                </button>
              </div>
            )}
            {errors.exterior_finish_color && (
              <p className="mt-1 text-sm text-red-400">{errors.exterior_finish_color}</p>
            )}
          </div>
        )}
      </div>

      {hasWrapOrPpf && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Brand (optional)</label>
            <input
              type="text"
              list="exterior-finish-brands"
              value={values.exterior_finish_brand}
              onChange={(e) => onChange('exterior_finish_brand', e.target.value)}
              className="input"
              placeholder="3M, XPEL, Avery Dennison..."
              disabled={disabled}
            />
            <datalist id="exterior-finish-brands">
              {EXTERIOR_FINISH_BRAND_OPTIONS.map((brand) => (
                <option key={brand} value={brand} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Finish style (optional)</label>
            <CustomDropdown
              value={values.exterior_finish_style}
              onChange={(value) => onChange('exterior_finish_style', value)}
              options={[
                { value: '', label: 'Not specified' },
                ...EXTERIOR_FINISH_STYLE_OPTIONS.map((style) => ({ value: style, label: style })),
              ]}
              placeholder="Gloss, satin, matte..."
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExteriorFinishFields;
